import SafeHorizontal from "@components/safe-horizontal";
import { Container, View } from "dripsy";
import * as React from "react";
import type { StyleProp, ViewStyle } from "react-native";

export interface StickyHeaderIndice {
  name: string;
  index: number;
}

export function extractKey(items: string | number | any[]) {
  if (typeof items === "string" || typeof items === "number") {
    return `${items}`;
  }
  const keyArr: string[] = [];
  items?.forEach((im: any) => {
    keyArr.push(`${im?.id || im?.pid}-${im?.extraCatKey || ""}`);
  });

  return keyArr.join("-");
}
export function splitChunks<ItemT>(data: ItemT[], groupSize: number): (string | ItemT[])[] {
  const results: (string | ItemT[])[] = [];

  let chunkIndex = 0;
  for (let i = 0; i < data.length; i++) {
    const item = data[i]!;

    const isHeader = typeof item === "string";
    if (isHeader) {
      results.push(item as any);
      chunkIndex++;
      continue;
    }
    const previousChunk = results[chunkIndex - 1];
    const previousItemArray = previousChunk as ItemT[];
    const previousWasHeader = typeof previousChunk === "string";

    // check if previous item was header => add array
    if (previousWasHeader) {
      results.push([item]);
      chunkIndex++;
      continue;
    }
    if (previousItemArray && previousItemArray.length < groupSize) {
      // add to chunk
      previousItemArray.push(item);
    } else {
      // add new chunk with item
      results.push([item]);
      chunkIndex++;
    }
  }
  return results;
}

export interface CustomListViewType<ItemT extends any> {
  renderItem: ({ data, index }: { data: ItemT; index: number }) => React.JSX.Element;
  data: ItemT[];
  itemHeight: number;
  scrollHandler?: (event: any) => void;
  px?: number;
  gap?: number;
  numColumns?: number;
  style?: StyleProp<ViewStyle>;
  pt?: number;
  emptyText?: string;
  onEndReached?: () => void;
  withContainer?: boolean;
  scrollToTopIfChanged?: boolean;
  isLoadingMore?: boolean;
}

export function calculateGap({
  gap,
  chunkIndex,
  index,
  chunkSize,
  numColumns,
}: {
  gap: number;
  chunkIndex: number;
  index: number;
  chunkSize: number;
  numColumns: number;
}) {
  return {
    top: chunkIndex > 0 ? gap / 2 : 0,
    bottom: chunkIndex < chunkSize - 1 ? gap / 2 : 0,
    right: index < numColumns - 1 ? gap / 2 : 0,
    left: index > 0 ? gap / 2 : 0,
  };
}

export function RenderComponent<ItemT>({
  item,
  gap,
  chunkIndex,
  numColumns,
  chunkSize,
  px,
  renderItem,
  withContainer,
  height,
}: {
  item: ItemT[];
  gap: number;
  chunkIndex: number;
  chunkSize: number;
  numColumns: number;
  px: number;
  renderItem: ({ data, index }: { data: ItemT; index: number }) => React.JSX.Element;
  withContainer?: boolean;
  height?: number;
}) {
  const inner = (
    <SafeHorizontal sx={{ flexDirection: "row", px }}>
      {item.map((it, index) => {
        const { top, bottom, left, right } = calculateGap({
          gap,
          chunkIndex,
          index,
          numColumns,
          chunkSize,
        });

        return (
          <View
            key={index} // only use index here! see https://shopify.github.io/flash-list/docs/fundamentals/performant-components#remove-key-prop
            sx={{
              flex: 1,
              maxWidth: `${100 / numColumns}%`,
              pb: bottom,
              pt: top,
              pr: right,
              pl: left,
              height,
            }}
          >
            {renderItem({ index: chunkIndex + index, data: it })}
          </View>
        );
      })}
    </SafeHorizontal>
  );
  return withContainer ? <Container>{inner}</Container> : inner;
}
